'use client'

import { CaretDownIcon } from '@repo/ui/CaretDownIcon'
import { CheckIcon } from '@repo/ui/CheckIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { LoadingState as LoadingSpinner } from '@repo/ui/LoadingState'
import { SearchIcon } from '@repo/ui/SearchIcon'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'

export const ReactSelectMenu = ({
  items,
  disabled,
  searchable = true,
  clearable = true,
  loading,
  value = null,
  required,
  label,
  onChange = () => {},
  valueKey = 'id',
  labelKey = 'description',
  idLabelKey = '',
  valueCustomRenderFunction,
  optionCustomRenderFunction,
  onSearchTermChange,
  sx,
  menuOffset,
  name = '',
  size = 'small',
  errors = {},
  showIdInResults = true,
  menuZindex = 1100,
  showSearchPrompt = false,
  multi = false,
  hideMultiCheck = false,
  showSelectAll = false,
  showDeselectAll = false,
  autoFocus = false,
  hasAdornment = false,
  placeholder = '',
  onAtBottom = () => {},
  menuWidth = null,
  creatable = false,
  inputValue,
  onInputChange,
  isValidNewOption,
  formatCreateLabel,
  onCreateOption
}) => {
  const theme = useTheme()
  const [showLabelInSelectedState, setShowLabelInSelectedState] = useState(false)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [options, setOptions] = useState(items)

  // Store props in a ref so memoized components always read current values
  // without causing react-select to unmount/remount option DOM nodes on every render.
  // Defining components inline in the components prop causes react-select to treat them
  // as new component types on every render, destroying and recreating all option DOM nodes.
  // This breaks click handling when a re-render occurs between mouseDown and click events.
  const propsRef = useRef({})
  propsRef.current = {
    optionCustomRenderFunction,
    valueCustomRenderFunction,
    idLabelKey,
    showIdInResults,
    labelKey,
    valueKey,
    showSearchPrompt,
    theme,
    disabled,
    size,
    menuIsOpen,
    hideMultiCheck
  }

  const stableComponents = useMemo(
    () => ({
      Option: (props) => (
        <Option
          {...props}
          customRenderFunction={propsRef.current.optionCustomRenderFunction}
          idLabelKey={propsRef.current.idLabelKey}
          showIdInResults={propsRef.current.showIdInResults}
          themeColors={propsRef.current.theme}
          hideMultiCheck={propsRef.current.hideMultiCheck}
        />
      ),
      SingleValue: (props) => (
        <SingleValue {...props} customRenderFunction={propsRef.current.valueCustomRenderFunction} />
      ),
      MultiValue: (props) => (
        <MultiValue
          {...props}
          idLabelKey={propsRef.current.idLabelKey}
          labelKey={propsRef.current.labelKey}
          valueKey={propsRef.current.valueKey}
        />
      ),
      IndicatorSeparator: () => null,
      DropdownIndicator: () => {
        const selectH = propsRef.current.size === 'small' ? 40 : 53
        return (
          <div
            style={{
              width: '36px',
              height: `${selectH - 4}px`,
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CaretDownIcon
              sx={{
                color: propsRef.current.disabled ? 'rgba(5, 28, 51, 0.30)' : 'rgba(5, 28, 51, 0.60)',
                transform: `rotate(${propsRef.current.menuIsOpen ? '-180' : '0'}deg)`,
                pointerEvents: 'none',
                position: 'relative'
              }}
            />
          </div>
        )
      },
      NoOptionsMessage: (props) => <NoOptionsMessage {...props} showSearchPrompt={propsRef.current.showSearchPrompt} />,
      LoadingMessage
    }),
    []
  )

  useEffect(() => {
    setShowLabelInSelectedState(Boolean(value) || menuIsOpen)
  }, [value, menuIsOpen])

  useEffect(() => {
    const options =
      items
        ?.filter((option) => !!option)
        ?.map((option) => {
          return {
            ...option,
            label: option?.label || option?.[labelKey] || option?.[valueKey],
            value: option?.value || option?.[valueKey]
          }
        }) || []

    setOptions(options)
  }, [items, valueKey, labelKey])

  const handleChange = (newValue) => {
    if (onChange) {
      if (multi) {
        onChange(newValue)
      } else {
        const selectedItem = newValue ? options.find((option) => option.value === newValue.value) : null
        onChange(selectedItem || newValue) // support values created via Creatable (not in options)
      }
    }
  }

  const handleSelectAll = () => {
    onChange(options)
  }

  const handleDeselectAll = () => {
    onChange([])
  }

  const getBorderColor = (menuIsOpen, errorMessage) => {
    if (errorMessage) return theme?.palette?.error?.main || 'red'
    if (menuIsOpen) return theme?.palette?.text?.primary || 'black'
    return 'rgba(0, 0, 0, 0.23)'
  }

  const selectHeight = size === 'small' ? 40 : 53

  if (value && !value.label && labelKey && valueKey) {
    value.label = value[labelKey]
    value.value = value[valueKey]
  }

  let errorMessage = ''
  if (errors && name && errors[name]) {
    errorMessage = errors[name] || ''
  }

  const borderColor = getBorderColor(menuIsOpen, errorMessage)

  const Component = creatable ? CreatableSelect : Select

  return (
    <MUIStack direction="row" sx={{ flex: 1, position: 'relative', ...sx }} data-testid="ReactSelectMenu">
      <MUIInputLabel
        focused={showLabelInSelectedState}
        shrink={showLabelInSelectedState}
        id={`${label}-label`}
        htmlFor={`${label}-select-menu`}
        required={required}
        className={errorMessage ? 'Mui-error' : ''}
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          zIndex: 1,
          top: showLabelInSelectedState ? 0 : size === 'small' ? '8px' : '15px',
          left: showLabelInSelectedState ? 0 : '11px',
          transition: 'all 200ms',
          transform: showLabelInSelectedState ? 'translate(10px, -7px) scale(0.75)' : 'translate(0, 0)',
          backgroundColor: showLabelInSelectedState ? 'white' : 'transparent',
          padding: '0 4px',
          color: 'text.secondary',
          mb: 0,
          '&.MuiInputLabel-shrink': {
            background: disabled ? 'linear-gradient(0deg, rgb(242, 242, 242), white) !important' : 'white !important'
          }
        }}
      >
        {errorMessage ? errorMessage : label}
      </MUIInputLabel>

      <Component
        onMenuScrollToBottom={(e) => {
          if (!onAtBottom || typeof onAtBottom !== 'function') return
          onAtBottom(e)
        }}
        components={stableComponents}
        required={required}
        aria-labelledby={`${label}-label`}
        inputId={`${label}-select-menu`}
        isClearable={clearable}
        backspaceRemovesValue={!multi}
        escapeClearsValue={!multi}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        onChange={handleChange}
        placeholder={placeholder || (!menuIsOpen || value ? '' : 'Type to search')}
        openMenuOnFocus
        isDisabled={disabled}
        onInputChange={(v, meta) => {
          if (onInputChange) onInputChange(v, meta)
          else if (onSearchTermChange) onSearchTermChange(v, meta)
          return v
        }}
        inputValue={inputValue}
        isLoading={loading}
        isSearchable={searchable}
        menuPortalTarget={document.body}
        closeMenuOnSelect={!multi}
        value={value}
        menuPlacement="auto"
        hideSelectedOptions={false}
        maxMenuHeight={400}
        autoFocus={autoFocus}
        isMulti={multi}
        options={options}
        isValidNewOption={isValidNewOption}
        formatCreateLabel={formatCreateLabel}
        onCreateOption={onCreateOption}
        styles={{
          control: (base) => ({
            ...base,
            minWidth: '200px',
            width: sx?.width || '100%',
            borderRadius: sx?.borderRadius || '4px',
            height: `${selectHeight}px`,
            cursor: 'pointer',
            paddingLeft: hasAdornment ? (size == 'small' ? '36px' : '50px') : '0px',
            boxShadow: menuIsOpen ? `0 0 0 1px ${borderColor}` : 'none',
            borderColor,
            '&:focus,&:hover': {
              boxShadow: `0 0 0 1px ${borderColor}`,
              borderColor
            }
          }),
          container: (base) => ({
            ...base,
            height: `${selectHeight}px`,
            left: '1px',
            width: 'calc(100% - 2px)'
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: menuZindex,
            transform: menuOffset ? `translateX(${menuOffset})` : 'none'
          }),
          menu: (base) => ({
            ...base,
            zIndex: 6,
            backgroundColor: 'white',
            padding: '8px',
            width: '100%',
            minWidth: menuWidth || '400px',
            maxWidth: '100%',
            boxShadow:
              '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)'
          }),
          option: (base) => ({
            ...base,
            height: 'auto',
            backgroundColor: 'white',
            marginBottom: '5px',
            fontFamily: theme?.typography?.fontFamily || 'Mulish',
            fontSize: '0.875rem',
            cursor: 'pointer',
            whiteSpace: 'normal',
            width: '100%',
            color: theme?.palette?.text?.primary || 'black',
            borderRadius: '4px',
            padding: '8px 16px',
            '&.select__option--is-focused,&:target,&:focus,&:hover,:focus-visible,:focus-within': {
              backgroundColor: 'rgba(80, 115, 195, 0.08)'
            },
            '&.select__option--is-selected': {
              backgroundColor: 'rgba(80, 115, 195, 0.2)'
            }
          }),
          valueContainer: (base) => ({
            ...base,
            fontFamily: theme?.typography?.fontFamily || 'Mulish',
            fontSize: '0.875rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            width: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            color: theme?.palette?.text?.primary || '',
            zIndex: 2
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: '8px 2px',
            cursor: 'pointer',
            color: disabled ? 'rgba(5, 28, 51, 0.30)' : 'rgba(5, 28, 51, 0.60)'
          })
        }}
      />

      {showSelectAll || showDeselectAll ? (
        <MUIStack
          direction="row"
          justifyContent="flex-end"
          gap={3}
          sx={{ width: '100%', position: 'absolute', right: 0, top: -40 }}
        >
          {showSelectAll && <TextLinkButton sx={{ width: 'auto' }} onClick={handleSelectAll} title="Select all" />}
          {showDeselectAll && <TextLinkButton sx={{ width: 'auto' }} onClick={handleDeselectAll} title="Select none" />}
        </MUIStack>
      ) : null}
    </MUIStack>
  )
}

ReactSelectMenu.propTypes = {
  size: PropTypes.string,
  height: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  clearable: PropTypes.bool,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  items: PropTypes.array,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  idLabelKey: PropTypes.string,
  sx: PropTypes.object,
  valueCustomRenderFunction: PropTypes.func,
  optionCustomRenderFunction: PropTypes.func,
  searchFunction: PropTypes.func,
  insertOptionFunction: PropTypes.func,
  required: PropTypes.bool,
  onSearchTermChange: PropTypes.func,
  menuOffset: PropTypes.string,
  errors: PropTypes.object,
  name: PropTypes.string,
  showIdInResults: PropTypes.bool,
  menuZindex: PropTypes.number,
  showSearchPrompt: PropTypes.bool,
  multi: PropTypes.bool,
  hideMultiCheck: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  showDeselectAll: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hasAdornment: PropTypes.bool,
  placeholder: PropTypes.any,
  onAtBottom: PropTypes.func,
  menuWidth: PropTypes.string,
  creatable: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  isValidNewOption: PropTypes.func,
  formatCreateLabel: PropTypes.func,
  onCreateOption: PropTypes.func
}

const Option = (props) => {
  const {
    isFocused,
    isSelected,
    isMulti,
    isDisabled,
    customRenderFunction,
    data,
    idLabelKey,
    showIdInResults,
    themeColors,
    hideMultiCheck
  } = props || {}
  const { value, label } = data
  const focusedClassName = `${isFocused ? 'select__option--is-focused' : ''}`
  const selectedClassName = `${isSelected ? 'select__option--is-selected' : ''}`
  const disabledClassName = `${isDisabled ? 'select__option--is-disabled' : ''}`
  const className = [focusedClassName, selectedClassName, disabledClassName].join(' ')
  const children = customRenderFunction ? customRenderFunction(data) : null

  return (
    <components.Option {...props} className={className}>
      {children || (
        <MUIStack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
        >
          {isMulti && !hideMultiCheck ? (
            <CheckIcon
              showCheck={isSelected}
              sx={{
                width: '20px',
                height: '20px',
                marginRight: '8px',
                color: themeColors?.palette?.text?.primary || 'black'
              }}
            />
          ) : null}

          <MUIStack
            direction="column"
            sx={{
              width: '100%',
              position: 'relative'
            }}
          >
            {value && showIdInResults ? (
              <MUITypography
                sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none', color: 'text.secondary' }}
                variant="body2"
                noWrap
              >
                {idLabelKey ? String(data[idLabelKey]).toUpperCase() : String(value)?.toUpperCase()}
              </MUITypography>
            ) : null}
            {label ? (
              <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1">
                {label}
              </MUITypography>
            ) : null}
          </MUIStack>
        </MUIStack>
      )}
    </components.Option>
  )
}

Option.propTypes = {
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isMulti: PropTypes.bool,
  customRenderFunction: PropTypes.func,
  data: PropTypes.object,
  idLabelKey: PropTypes.string,
  showIdInResults: PropTypes.bool,
  themeColors: PropTypes.object,
  hideMultiCheck: PropTypes.bool
}

const SingleValue = (props) => {
  const { customRenderFunction, data } = props
  const children = customRenderFunction ? customRenderFunction(data) : null
  const tooltipTitle = data?.label || ''

  return (
    <components.SingleValue {...props}>
      {children || (
        <MUITooltip title={tooltipTitle} placement="top">
          <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1" noWrap>
            {tooltipTitle}
          </MUITypography>
        </MUITooltip>
      )}
    </components.SingleValue>
  )
}

SingleValue.propTypes = {
  customRenderFunction: PropTypes.func,
  getValue: PropTypes.func,
  data: PropTypes.object,
  hasAdornment: PropTypes.bool
}

const MultiValue = (props) => {
  const { getValue, index, idLabelKey, labelKey, valueKey } = props
  if (index !== 0) return null

  const value = getValue()

  if (!value?.length) {
    return (
      <components.SingleValue {...props}>
        <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1" noWrap>
          {'Please select'}
        </MUITypography>
      </components.SingleValue>
    )
  }

  const firstSelection = value[0]
  const count = value.length
  const additionalCount = count - 1
  const firstSelectionLabel = firstSelection[labelKey] || firstSelection[idLabelKey] || firstSelection[valueKey]
  const suffix = count === 1 ? '' : ` + ${additionalCount} other${additionalCount === 1 ? '' : 's'}`
  const tooltipTitle = `${firstSelectionLabel ?? ''}${suffix}`

  return (
    <components.SingleValue {...props}>
      <MUITooltip title={tooltipTitle} placement="top">
        <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1" noWrap>
          {firstSelectionLabel}
          {suffix}
        </MUITypography>
      </MUITooltip>
    </components.SingleValue>
  )
}

MultiValue.propTypes = {
  getValue: PropTypes.func,
  index: PropTypes.number,
  idLabelKey: PropTypes.string,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string
}

const NoOptionsMessage = ({ showSearchPrompt }) => {
  const title = showSearchPrompt ? 'Search to show results' : 'No matching results'
  return <EmptyState text={title} icon={<SearchIcon sx={{ fontSize: 36, mb: 4 }} />} />
}

NoOptionsMessage.propTypes = {
  showSearchPrompt: PropTypes.bool
}

const LoadingMessage = () => {
  return (
    <MUIStack
      direction="column"
      justifyContent="center"
      alignItems="center"
      style={{ width: '100%', height: '100%', minHeight: '240px', overflowX: 'hidden', overflowY: 'hidden' }}
    >
      <LoadingSpinner />
      <MUITypography variant="body1" sx={{ mt: 4 }}>
        Loading
      </MUITypography>
    </MUIStack>
  )
}
